import { useParams, Link } from 'react-router-dom';
import PageMeta from '@/components/base/PageMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import DramaCard from '@/components/base/DramaCard';
import { getDramaById, getRelatedDramas } from '@/mocks/dramas';

export default function DramaDetail() {
  const { id } = useParams<{ id: string }>();
  const drama = getDramaById(id || '');

  if (!drama) {
    return (
      <div className="w-full px-4 md:px-6 pt-24 pb-14">
        <div className="max-w-7xl mx-auto text-center py-20">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-background-100 rounded-full">
            <i className="ri-file-unknow-line text-foreground-400 text-3xl"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-950 mb-2">剧集未找到</h1>
          <p className="text-foreground-500 text-sm mb-6">抱歉，您要查找的剧集不存在或已被移除</p>
          <Link
            to="/"
            className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedDramas = getRelatedDramas(drama.id);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://tiantiancinema.com/' },
      { '@type': 'ListItem', position: 2, name: drama.category, item: `https://tiantiancinema.com/category/${drama.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: drama.title, item: `https://tiantiancinema.com/drama/${drama.id}` },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${drama.title} - ${drama.year}年${drama.category}全集在线观看`,
    description: drama.description,
    author: { '@type': 'Person', name: drama.director },
    datePublished: `${drama.year}-01-01`,
    publisher: { '@type': 'Organization', name: '天天影院' },
  };

  return (
    <>
      <PageMeta
        title={`${drama.title}全集在线观看 - 天天影院`}
        description={`${drama.title}(${drama.year})${drama.category}全集在线观看。${drama.description}`}
        keywords={`${drama.title},${drama.title}全集,${drama.category}在线观看,天天影院`}
        canonicalUrl={`https://tiantiancinema.com/drama/${drama.id}`}
        ogTitle={`${drama.title}全集在线观看 - 天天影院`}
        ogDescription={drama.description}
        ogType="article"
        ogUrl={`https://tiantiancinema.com/drama/${drama.id}`}
        schemaObjects={[breadcrumbSchema, articleSchema]}
      />

      <div className="w-full px-4 md:px-6 pt-24 pb-14">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: drama.category, href: `/category/${drama.categorySlug}` },
              { label: drama.title },
            ]}
          />

          <div className="flex flex-col lg:flex-row gap-8 mt-6">
            <div className="flex-shrink-0">
              <div className="w-full sm:w-60 lg:w-72 aspect-[3/4] rounded-lg overflow-hidden border border-background-200/70">
                <img
                  src={drama.posterImage}
                  alt={`${drama.title}海报`}
                  title={`${drama.title} - ${drama.year}年${drama.category}`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                {drama.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full whitespace-nowrap">
                  {drama.category}
                </span>
                <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full whitespace-nowrap">
                  {drama.rating} 分
                </span>
                <span className={`px-3 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
                  drama.updateStatus === '已完结'
                    ? 'bg-secondary-100 text-secondary-700'
                    : 'bg-accent-100 text-accent-700'
                }`}>
                  {drama.updateStatus}
                </span>
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                {drama.longDescription}
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
                <div className="bg-background-100 rounded-lg p-3">
                  <span className="text-xs text-foreground-500 block mb-1">导演</span>
                  <span className="text-sm font-medium text-foreground-950 whitespace-nowrap">{drama.director}</span>
                </div>
                <div className="bg-background-100 rounded-lg p-3">
                  <span className="text-xs text-foreground-500 block mb-1">年份</span>
                  <span className="text-sm font-medium text-foreground-950">{drama.year}</span>
                </div>
                <div className="bg-background-100 rounded-lg p-3">
                  <span className="text-xs text-foreground-500 block mb-1">集数</span>
                  <span className="text-sm font-medium text-foreground-950">{drama.totalEpisodes}集</span>
                </div>
                <div className="bg-background-100 rounded-lg p-3">
                  <span className="text-xs text-foreground-500 block mb-1">地区</span>
                  <span className="text-sm font-medium text-foreground-950 whitespace-nowrap">{drama.region}</span>
                </div>
              </div>

              <div className="mb-4">
                <span className="text-xs text-foreground-500 block mb-2">主演</span>
                <div className="flex flex-wrap gap-2">
                  {drama.actors.map((actor) => (
                    <span
                      key={actor}
                      className="px-3 py-1 bg-background-100 text-foreground-700 text-xs rounded-full whitespace-nowrap"
                    >
                      {actor}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-lg font-bold text-foreground-950 mb-5">分集列表</h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-2">
              {drama.episodes.map((ep) => (
                <div
                  key={ep.id}
                  className="bg-background-100 border border-background-200/70 rounded-lg p-3 text-center hover:border-primary-300 hover:bg-primary-50/50 transition-all cursor-pointer"
                >
                  <span className="text-sm font-medium text-foreground-950 block whitespace-nowrap">{ep.title}</span>
                  <span className="text-xs text-foreground-500 mt-1 block whitespace-nowrap">{ep.duration}</span>
                </div>
              ))}
            </div>
          </div>

          {relatedDramas.length > 0 && (
            <div className="mt-14">
              <h2 className="text-lg font-bold text-foreground-950 mb-5">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedDramas.map((d) => (
                  <DramaCard key={d.id} drama={d} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}